import React, { useEffect, useState } from "react";

import { setGlobalAuthChecked } from "~/components/layout/utils/authState";
import { identifyUser, resetUser } from "~/integrations/posthog/client";
import { supabase } from "~/integrations/supabase/client";
import { Sentry } from "~/lib/sentry";
import { useProfileStore } from "~/store/profileStore";

interface ProfileProviderProps {
  children: React.ReactNode;
}

export function ProfileProvider({ children }: ProfileProviderProps) {
  // NOTE: Removed fetchProfile and clearProfile selectors - using getState() instead
  // to avoid infinite rerenders (actions in deps cause new refs each render)
  const userId = useProfileStore((state) => state.id);
  const [isInitialized, setIsInitialized] = useState(false);

  // Safely fetch profile with retry logic
  const fetchProfileSafely = async () => {
    try {
      const { fetchProfile } = useProfileStore.getState();
      await fetchProfile();

      // If userId is still not set after fetchProfile, try again after a delay
      setTimeout(() => {
        const currentUserId = useProfileStore.getState().id;
        if (!currentUserId) {
          const { fetchProfile: refetch } = useProfileStore.getState();
          refetch();
        } else {
          // Set Sentry user context when profile is loaded
          const profile = useProfileStore.getState();
          Sentry.setUser({
            id: profile.id || "unknown",
            email: profile.email || "unknown",
            username:
              `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
              "unknown",
          });

          // Identify user in PostHog
          identifyUser(profile.id || "unknown", {
            email: profile.email,
            first_name: profile.first_name,
            last_name: profile.last_name,
            role: profile.role,
          });
        }
      }, 500);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  useEffect(() => {
    // Set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Using setTimeout to avoid potential deadlocks with Supabase auth state changes
        setTimeout(() => {
          fetchProfileSafely();
        }, 100);
      } else if (event === "SIGNED_OUT") {
        // Clear everything when signed out
        const { clearProfile } = useProfileStore.getState();
        clearProfile();
        localStorage.removeItem("userRole");
        localStorage.removeItem("profile-storage");
        setGlobalAuthChecked(false);

        // Clear Sentry user context
        Sentry.setUser(null);

        // Reset PostHog user
        resetUser();
      }
    });

    // Initial fetch of profile if user is already logged in
    const initializeProfile = async () => {
      const { data } = await supabase.auth.getSession();

      if (data.session) {
        await fetchProfileSafely();
      }

      setIsInitialized(true);
    };

    initializeProfile();

    return () => {
      subscription.unsubscribe();
    };
  }, []); // Empty deps - actions retrieved via getState() inside to avoid infinite rerenders

  if (!isInitialized) {
    // You could return a loading spinner here if needed
    return null;
  }

  return <>{children}</>;
}

export default ProfileProvider;
